import { useState, useEffect } from "react";
import { api, MODELS, SECTION_KEYS, DISCIPLINES, GEOGRAPHIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Sparkles, Loader2, ShieldCheck, Wand2, Check, AlertTriangle, RefreshCw, BookOpen } from "lucide-react";

/**
 * AIFullProject — Idea → outline → confirmation → generate all sections + references.
 * Props:
 *  - module: 'proposal' | 'paper' | 'thesis' | 'consultancy'
 *  - title?: string      (existing document title, seeds the field)
 *  - defaultIdea?: string
 *  - discipline?: string
 *  - geography?: string
 *  - sectionKeys?: string[]                      (override which sections to generate; e.g. paper's active template)
 *  - sectionLabels?: { key: string }             (human-friendly labels for the above; falls back to prettified key)
 *  - buttonLabel?: string    (default: 'Generate full project')
 *  - dialogTitle?: string    (defaults to "Generate full {module} from an idea")
 *  - docId?: string          (backend id of the current doc; enables Download PDF / Word after Apply)
 *  - saveDoc?: () => Promise (imperative save handler from parent — used before download)
 *  - onApply({ suggested_title, outline, sections, references }) — called when user clicks Apply
 *  - testidPrefix?: string
 */
export default function AIFullProject({
  module = "proposal",
  title = "",
  defaultIdea = "",
  discipline = "Sciences",
  geography = "Global",
  sectionKeys,
  sectionLabels,
  journalTemplate,
  buttonLabel,
  dialogTitle,
  docId,
  saveDoc,
  onApply,
  testidPrefix = "fp",
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: idea, 2: review outline, 3: generating, 4: done
  const [idea, setIdea] = useState(defaultIdea);
  const [projTitle, setProjTitle] = useState(title);
  const [disc, setDisc] = useState(discipline);
  const [geo, setGeo] = useState(geography);
  const [modelIdx, setModelIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null); // outline + section_plan + refs + problem_statement
  const [problemStatement, setProblemStatement] = useState("");
  const [selected, setSelected] = useState({}); // section_key → bool
  const [includeRefs, setIncludeRefs] = useState(true);
  const [ack, setAck] = useState(false); // user permission
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [sections, setSections] = useState({}); // key → html
  // Document generation settings (professor-controlled)
  const [citationStyle, setCitationStyle] = useState(journalTemplate === "IEEE (Standard)" ? "IEEE" : journalTemplate === "ACM" ? "ACM" : journalTemplate === "Nature (Brief)" ? "Nature" : "IEEE");
  const [refPlacement, setRefPlacement] = useState("endnotes"); // 'footer' or 'endnotes'
  const [includeFigures, setIncludeFigures] = useState(true);
  const [wordTarget, setWordTarget] = useState(7000);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setStep(1); setPlan(null); setSections({}); setProgress({done:0,total:0,current:""}); setAck(false);
      setProblemStatement("");
      setIdea(defaultIdea); setProjTitle(title); setDisc(discipline); setGeo(geography);
    }
  }, [open, defaultIdea, title, discipline, geography]);

  const runOutline = async () => {
    if (idea.trim().length < 12) return toast.error("Please describe your idea in at least 12 characters.");
    setBusy(true);
    try {
      const m = MODELS[modelIdx];
      const { data } = await api.post("/ai/full-project/outline", {
        idea, title: projTitle, module, discipline: disc, geography: geo,
        section_keys: sectionKeys && sectionKeys.length ? sectionKeys : undefined,
        journal_template: journalTemplate || undefined,
        model_provider: m.provider, model_name: m.name,
      });
      setPlan(data);
      setProblemStatement(data.problem_statement || "");
      // Pre-select all sections that the server returns (client-supplied wins).
      const keys = (sectionKeys && sectionKeys.length) ? sectionKeys : (data.section_keys || SECTION_KEYS.map(s=>s.key));
      const sel = {};
      keys.forEach(k => { sel[k] = true; });
      setSelected(sel);
      if (!projTitle && data.suggested_title) setProjTitle(data.suggested_title);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not plan the project — try again.");
    } finally { setBusy(false); }
  };

  const prettify = (k) => (sectionLabels && sectionLabels[k]) || (SECTION_KEYS.find(s => s.key === k)?.label) || k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const runFullGeneration = async () => {
    if (!ack) return toast.error("Please tick the confirmation before we generate the full project.");
    if ((problemStatement || "").trim().length < 40) return toast.error("Please refine the Problem Statement (at least 40 characters) before generating.");
    const keys = Object.entries(selected).filter(([_,v]) => v).map(([k])=>k);
    if (keys.length === 0) return toast.error("Pick at least one section to generate.");
    setStep(3); setSections({}); setProgress({ done: 0, total: keys.length, current: keys[0] });
    const m = MODELS[modelIdx];
    const refs = includeRefs ? (plan?.references || []) : [];
    const acc = {};
    // Serial generation so we stay well under ingress timeouts per-call
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      setProgress({ done: i, total: keys.length, current: k });
      try {
        const { data } = await api.post("/ai/full-project/section", {
          idea, title: projTitle, module, section_key: k, outline: plan?.outline || "",
          problem_statement: problemStatement,
          discipline: disc, geography: geo, references: refs,
          journal_template: journalTemplate || undefined,
          citation_style: citationStyle,
          reference_placement: refPlacement,
          include_figures: includeFigures,
          word_target: wordTarget,
          model_provider: m.provider, model_name: m.name,
        });
        acc[k] = data.html || "";
        setSections({ ...acc });
      } catch (e) {
        acc[k] = `<p><em>Could not draft this section — please retry. (${e?.response?.data?.detail || 'error'})</em></p>`;
        setSections({ ...acc });
      }
    }
    setProgress({ done: keys.length, total: keys.length, current: "" });
    // Ensure the user-approved Problem Statement is what ends up in the final doc
    if (keys.includes("problem_statement") && problemStatement.trim()) {
      const paras = problemStatement.trim().split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      acc["problem_statement"] = paras;
      setSections({ ...acc });
    }
    setStep(4);
    toast.success(`All ${keys.length} section${keys.length===1?"":"s"} drafted — review before applying.`);
  };

  const retrySection = async (k) => {
    setProgress(p => ({ ...p, current: k }));
    const m = MODELS[modelIdx];
    const refs = includeRefs ? (plan?.references || []) : [];
    try {
      const { data } = await api.post("/ai/full-project/section", {
        idea, title: projTitle, module, section_key: k, outline: plan?.outline || "",
        problem_statement: problemStatement,
        discipline: disc, geography: geo, references: refs,
        journal_template: journalTemplate || undefined,
        citation_style: citationStyle,
        reference_placement: refPlacement,
        include_figures: includeFigures,
        word_target: wordTarget,
        model_provider: m.provider, model_name: m.name,
      });
      setSections(s => ({ ...s, [k]: data.html || "" }));
      toast.success(`Redrafted ${k}`);
    } catch { toast.error("Retry failed"); }
  };

  const applyToDoc = () => {
    onApply?.({
      suggested_title: projTitle,
      outline: plan?.outline || "",
      problem_statement: problemStatement,
      sections,
      references: includeRefs ? (plan?.references || []) : [],
    });
    setOpen(false);
    toast.success("Full project applied — review each section, then download from the top bar.");
  };

  const MODULE_TO_PATH = { proposal: "proposals", paper: "papers", thesis: "thesis", consultancy: "consultancy" };
  const downloadFinal = async (fmt /* 'pdf' | 'docx' */) => {
    if (!docId) return toast.error("Save the document first, then download.");
    // Ensure the freshly generated content is applied + persisted first
    try {
      onApply?.({
        suggested_title: projTitle,
        outline: plan?.outline || "",
        problem_statement: problemStatement,
        sections,
        references: includeRefs ? (plan?.references || []) : [],
      });
      if (saveDoc) {
        await saveDoc();
      } else {
        // Wait a beat for the autosave path to fire in the parent
        await new Promise(r => setTimeout(r, 1600));
      }
      const path = MODULE_TO_PATH[module] || "proposals";
      const token = localStorage.getItem("gf_token");
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/${path}/${docId}/export/${fmt}`, { headers: { Authorization: `Bearer ${token}` }});
      if (!res.ok) return toast.error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(projTitle || module).replace(/[^\w-]+/g, "_")}.${fmt}`;
      a.click();
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("Download failed — try Apply first, then download from the top bar.");
    }
  };

  return (
    <>
      <Button onClick={()=>setOpen(true)} className="rounded-full" data-testid={`${testidPrefix}-open`}>
        <Wand2 className="w-4 h-4 mr-2"/>{buttonLabel || "Generate full project"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary"/>{dialogTitle || `Generate full ${module} from an idea`}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Idea + settings */}
          {step === 1 && (
            <div className="space-y-4" data-testid={`${testidPrefix}-step1`}>
              <div>
                <Label>Your idea in one paragraph</Label>
                <Textarea rows={5} placeholder="e.g., Design a low-cost AI-guided tele-audiometry platform for hearing screening in rural PHCs of India, combining smartphone-based pure-tone audiometry with ASHA-worker-friendly UX."
                  value={idea} onChange={(e)=>setIdea(e.target.value)} data-testid={`${testidPrefix}-idea`}/>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Working title (optional)</Label><Input value={projTitle} onChange={(e)=>setProjTitle(e.target.value)} data-testid={`${testidPrefix}-title`}/></div>
                <div><Label>Model</Label>
                  <Select value={String(modelIdx)} onValueChange={(v)=>setModelIdx(Number(v))}>
                    <SelectTrigger data-testid={`${testidPrefix}-model`}><SelectValue/></SelectTrigger>
                    <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Discipline</Label>
                  <Select value={disc} onValueChange={setDisc}><SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Geography</Label>
                  <Select value={geo} onValueChange={setGeo}><SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{GEOGRAPHIES.map(g=><SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 text-xs flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary"/>
                <span>The AI will first propose a project outline. You'll review it and confirm before we generate every section. AI may make mistakes — always check before finalising.</span>
              </div>
            </div>
          )}

          {/* Step 2: Review outline + pick sections + refs + acknowledge */}
          {step === 2 && plan && (
            <div className="space-y-4" data-testid={`${testidPrefix}-step2`}>
              <div className="card-crisp rounded-lg p-4">
                <div className="overline mb-1">Suggested title</div>
                <div className="font-display text-2xl leading-tight" data-testid={`${testidPrefix}-suggested-title`}>{plan.suggested_title || projTitle}</div>
              </div>
              <div className="rounded-lg p-4 border-2 border-success/40 bg-success/10">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="overline text-success">Step 1 · Problem Statement <span className="ml-2 px-2 py-0.5 rounded-full bg-success text-white text-[10px]">AI-drafted · edit before proceeding</span></div>
                    <div className="text-xs text-muted-foreground mt-1">Everything the AI writes next will be anchored to this problem statement.</div>
                  </div>
                </div>
                <Textarea
                  rows={7}
                  value={problemStatement}
                  onChange={(e)=>setProblemStatement(e.target.value)}
                  placeholder="Describe the specific problem, the gap in current knowledge/practice, its significance, and why it must be solved now."
                  data-testid={`${testidPrefix}-problem-statement`}
                />
                <div className="text-[11px] text-muted-foreground mt-1">{(problemStatement || "").trim().split(/\s+/).filter(Boolean).length} words · aim for 200–300</div>
              </div>
              <div className="card-crisp rounded-lg p-4">
                <div className="overline mb-1">Project outline</div>
                <p className="text-sm leading-relaxed">{plan.outline}</p>
              </div>

              {/* Document generation settings */}
              <div className="card-crisp rounded-lg p-4">
                <div className="overline mb-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-primary"/>Document settings</div>
                <div className="grid md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Citation style</label>
                    <select value={citationStyle} onChange={(e)=>setCitationStyle(e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid={`${testidPrefix}-citation-style`}>
                      {["IEEE","Scopus","Nature","Springer","Elsevier","ACM","Harvard","Oxford","Stanford"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Reference placement</label>
                    <select value={refPlacement} onChange={(e)=>setRefPlacement(e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid={`${testidPrefix}-ref-placement`}>
                      <option value="endnotes">Endnotes (at document end)</option>
                      <option value="footer">Footnotes (numbered, per page)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Word target (whole doc)</label>
                    <input type="number" min="1000" max="10000" step="500" value={wordTarget} onChange={(e)=>setWordTarget(Number(e.target.value)||7000)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid={`${testidPrefix}-word-target`}/>
                  </div>
                  <div className="flex items-end gap-2">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={includeFigures} onChange={(e)=>setIncludeFigures(e.target.checked)} data-testid={`${testidPrefix}-figures`}/>
                      Include figures / charts
                    </label>
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Recommended: 6,000–8,000 words. Style rules are strictly enforced during generation. Switching style here regenerates with the new citation format.
                </div>
              </div>
              <div className="card-crisp rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="overline">Sections to generate</div>
                    <div className="text-xs text-muted-foreground">Untick anything you'd rather write yourself.</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{Object.values(selected).filter(Boolean).length}/{SECTION_KEYS.length} selected</div>
                </div>
                <div className="grid md:grid-cols-2 gap-2 max-h-[280px] overflow-auto pr-1">
                  {((sectionKeys && sectionKeys.length) ? sectionKeys : (plan.section_keys || SECTION_KEYS.map(s=>s.key))).map(k => (
                    <label key={k} className="flex items-start gap-2 p-2 rounded-md border border-border hover:bg-secondary/50 cursor-pointer">
                      <Checkbox checked={!!selected[k]} onCheckedChange={(v)=>setSelected(s=>({...s, [k]:!!v}))} data-testid={`${testidPrefix}-sec-${k}`}/>
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{prettify(k)}</div>
                        <div className="text-[11px] text-muted-foreground line-clamp-2">{plan.section_plan?.[k] || "—"}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="card-crisp rounded-lg p-4 flex items-start gap-3">
                <BookOpen className="w-5 h-5 text-primary shrink-0 mt-0.5"/>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg">References</div>
                    <label className="text-xs flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={includeRefs} onCheckedChange={setIncludeRefs} data-testid={`${testidPrefix}-include-refs`}/>
                      Include & cite from these papers
                    </label>
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">Curated from OpenAlex using your project's aims.</div>
                  {(plan.references || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No references were retrieved for this topic. You can still generate — citations will be omitted.</div>
                  ) : (
                    <ul className="text-xs list-decimal pl-4 max-h-[140px] overflow-auto space-y-0.5" data-testid={`${testidPrefix}-refs-list`}>
                      {plan.references.map((r,i)=>(
                        <li key={i}>
                          <b>{(r.authors||[])[0] || ""}{(r.authors||[]).length>1?" et al.":""}</b> ({r.year||"—"}). {r.title} <span className="text-muted-foreground">{r.venue||""}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="border-t border-border pt-3">
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <Checkbox checked={ack} onCheckedChange={setAck} data-testid={`${testidPrefix}-ack`}/>
                  <span>
                    I have reviewed the outline and give permission for the AI to draft <b>all selected sections</b>.
                    I understand the output is a first draft and I will verify it before finalising.
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Step 3: Streaming generation progress */}
          {step === 3 && (
            <div className="space-y-3 py-6" data-testid={`${testidPrefix}-step3`}>
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-primary"/>
                <div className="font-display text-xl">Drafting section {progress.done + 1} of {progress.total}…</div>
              </div>
              <div className="text-sm text-muted-foreground">Currently writing: <b>{progress.current || "…"}</b></div>
              <div className="w-full h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${(progress.done / Math.max(1,progress.total)) * 100}%` }}/>
              </div>
              <div className="text-xs text-muted-foreground italic">Each section is a separate request so we never time out. You can safely leave this dialog open.</div>
            </div>
          )}

          {/* Step 4: Review + Apply */}
          {step === 4 && (
            <div className="space-y-3 max-h-[420px] overflow-auto" data-testid={`${testidPrefix}-step4`}>
              {Object.keys(sections).length === 0 && <div className="text-sm text-muted-foreground">No sections were generated.</div>}
              {Object.entries(sections).map(([k, html]) => (
                <details key={k} className="card-crisp rounded-lg p-3">
                  <summary className="cursor-pointer flex items-center justify-between">
                    <span className="font-medium text-sm flex items-center gap-2"><Check className="w-4 h-4 text-emerald-700"/>{prettify(k)}</span>
                    <button onClick={(e)=>{e.preventDefault(); retrySection(k);}} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`${testidPrefix}-retry-${k}`}><RefreshCw className="w-3 h-3"/>Redraft</button>
                  </summary>
                  <div className="prose-editor mt-2 text-sm max-h-[260px] overflow-auto" dangerouslySetInnerHTML={{__html: html}}/>
                </details>
              ))}
              <div className="rounded-md border border-dashed border-primary/50 bg-primary/5 p-2.5 text-xs flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary"/>
                <span>AI may make mistakes — review each section carefully before finalising. Then download as PDF/Word from the main page.</span>
              </div>
            </div>
          )}

          <DialogFooter>
            {step === 1 && (
              <>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={runOutline} disabled={busy || idea.trim().length < 12} data-testid={`${testidPrefix}-plan`}>
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Planning…</> : <><Sparkles className="w-4 h-4 mr-2"/>Plan project</>}
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button variant="outline" onClick={()=>setStep(1)}>Back</Button>
                <Button onClick={runFullGeneration} disabled={!ack || (problemStatement || "").trim().length < 40} data-testid={`${testidPrefix}-generate`}>
                  <ShieldCheck className="w-4 h-4 mr-2"/>Approve Problem Statement · Generate full project
                </Button>
              </>
            )}
            {step === 3 && (
              <Button variant="outline" disabled>Generating…</Button>
            )}
            {step === 4 && (
              <>
                <Button variant="outline" onClick={()=>setStep(2)}>Adjust sections</Button>
                {docId && module !== "consultancy" && (
                  <>
                    <Button variant="outline" onClick={()=>downloadFinal("pdf")} data-testid={`${testidPrefix}-dl-pdf`}>Download PDF</Button>
                    <Button variant="outline" onClick={()=>downloadFinal("docx")} data-testid={`${testidPrefix}-dl-docx`}>Download Word</Button>
                  </>
                )}
                {docId && module === "consultancy" && (
                  <>
                    <Button variant="outline" onClick={()=>downloadFinal("pdf")} data-testid={`${testidPrefix}-dl-pdf`}>Download PDF</Button>
                    <Button variant="outline" onClick={()=>downloadFinal("docx")} data-testid={`${testidPrefix}-dl-docx`}>Download Word</Button>
                  </>
                )}
                <Button onClick={applyToDoc} data-testid={`${testidPrefix}-apply`}>
                  <Check className="w-4 h-4 mr-2"/>Apply to my {module}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
